<template>
  <div class="min-h-screen bg-[radial-gradient(circle_at_top,_#fef3c7,_#fff_30%,_#f8fafc_100%)]">
    <section class="mx-auto flex min-h-screen max-w-6xl items-center px-4 py-10">
      <div class="grid w-full gap-8 lg:grid-cols-[1.15fr_0.85fr]">
        <article class="overflow-hidden rounded-[32px] border border-amber-100 bg-white shadow-[0_25px_80px_rgba(146,64,14,0.12)]">
          <div class="border-b border-amber-100 bg-[linear-gradient(135deg,_#111827,_#1f2937_58%,_#92400e)] px-6 py-8 text-white sm:px-8">
            <div class="flex items-center gap-4">
              <img :src="logo" alt="Logotipo Asador San Miguel" class="h-14 w-14 rounded-full border border-white/20 bg-white/95 object-contain p-1.5 shadow-lg">
              <div>
                <p class="text-xs uppercase tracking-[0.42em] text-amber-200">Asador San Miguel</p>
                <h1 class="mt-2 text-3xl font-semibold">{{ heroTitle }}</h1>
              </div>
            </div>
            <p class="mt-6 max-w-2xl text-sm leading-6 text-amber-50/90 sm:text-base">
              {{ heroDescription }}
            </p>
          </div>

          <div class="space-y-6 px-6 py-8 sm:px-8">
            <div :class="statusCardClass" class="rounded-[28px] border p-5 sm:p-6">
              <div class="flex items-start gap-4">
                <div :class="iconClass" class="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full text-2xl shadow-sm">
                  <span v-if="uiState === 'success'">✓</span>
                  <span v-else-if="uiState === 'waiting'">...</span>
                  <span v-else>!</span>
                </div>
                <div class="space-y-3">
                  <div>
                    <p class="text-xs uppercase tracking-[0.28em] opacity-75">Estado del pago</p>
                    <h2 class="mt-2 text-2xl font-semibold">{{ statusTitle }}</h2>
                  </div>
                  <p class="max-w-2xl text-sm leading-6 sm:text-base">
                    {{ statusMessage }}
                  </p>
                  <p v-if="showCountdown" class="text-sm font-medium">
                    Volveremos al inicio en {{ redirectCountdown }} segundos.
                  </p>
                </div>
              </div>
            </div>

            <div v-if="verificationError" class="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {{ verificationError }}
            </div>

            <div class="grid gap-4 md:grid-cols-2">
              <article class="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4">
                <p class="text-xs uppercase tracking-[0.2em] text-gray-500">Reservación</p>
                <p class="mt-2 text-lg font-semibold text-gray-900">{{ reservationId || 'Pendiente' }}</p>
              </article>
              <article class="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4">
                <p class="text-xs uppercase tracking-[0.2em] text-gray-500">Sesión Clover</p>
                <p class="mt-2 break-all text-sm font-semibold text-gray-900">{{ checkoutSessionId || 'Sin sesión visible' }}</p>
              </article>
              <article v-if="paymentId" class="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4">
                <p class="text-xs uppercase tracking-[0.2em] text-gray-500">Payment ID</p>
                <p class="mt-2 break-all text-sm font-semibold text-gray-900">{{ paymentId }}</p>
              </article>
              <article class="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4">
                <p class="text-xs uppercase tracking-[0.2em] text-gray-500">Siguiente paso</p>
                <p class="mt-2 text-sm font-medium text-gray-900">{{ nextStepLabel }}</p>
              </article>
            </div>

            <div class="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                class="inline-flex items-center justify-center rounded-full bg-amber-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-amber-200 transition hover:bg-amber-700 focus:outline-none focus:ring-4 focus:ring-amber-200"
                @click="goHome"
              >
                Volver al inicio
              </button>
              <button
                v-if="canRetryVerification"
                type="button"
                class="inline-flex items-center justify-center rounded-full border border-gray-300 bg-white px-6 py-3 text-sm font-semibold text-gray-900 transition hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-gray-200"
                :disabled="isChecking"
                @click="retryManualVerification"
              >
                {{ isChecking ? 'Validando...' : 'Intentar verificación manual' }}
              </button>
            </div>
          </div>
        </article>

        <aside class="space-y-4">
          <div class="rounded-[28px] border border-white/70 bg-white/90 p-6 shadow-[0_20px_50px_rgba(15,23,42,0.08)] backdrop-blur">
            <p class="text-xs uppercase tracking-[0.28em] text-gray-500">Qué sigue</p>
            <ol class="mt-4 space-y-4 text-sm text-gray-600">
              <li class="flex gap-3">
                <span class="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-semibold text-amber-700">1</span>
                <span>Procesamos la respuesta de Clover y verificamos si el pago ya quedó registrado en tu solicitud.</span>
              </li>
              <li class="flex gap-3">
                <span class="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-semibold text-amber-700">2</span>
                <span>Si el pago fue aprobado, tu solicitud entra al panel del restaurante como <strong class="text-gray-900">pendiente</strong> para revisión final.</span>
              </li>
              <li class="flex gap-3">
                <span class="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-semibold text-amber-700">3</span>
                <span>Cuando el restaurante la confirme, recibirás el QR final con los detalles completos por correo electrónico.</span>
              </li>
            </ol>
          </div>

          <div class="rounded-[28px] border border-gray-200 bg-white p-6 shadow-[0_20px_50px_rgba(15,23,42,0.08)]">
            <p class="text-xs uppercase tracking-[0.28em] text-gray-500">Contacto</p>
            <div class="mt-4 space-y-3 text-sm text-gray-600">
              <p><strong class="text-gray-900">Asador San Miguel</strong></p>
              <p>CARR 152 KM 11.0 BO CEDRO ARRIBA, Naranjito, PR 00719</p>
              <p><a class="font-semibold text-amber-700 hover:text-amber-800" href="tel:+17879004682">787-900-4682</a></p>
              <p><a class="font-semibold text-amber-700 hover:text-amber-800" href="mailto:reservacionesasm@gmail.com">reservacionesasm@gmail.com</a></p>
            </div>
          </div>
        </aside>
      </div>
    </section>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  getReservationPaymentStatus as getReservationPaymentStatusRequest,
  verifyReservationPayment as verifyReservationPaymentRequest
} from '../services/api/reservations';
import logoImage from '../assets/logo.png';

const MAX_VERIFY_ATTEMPTS = 5;
const VERIFY_DELAY_MS = 4000;
const REDIRECT_DELAY_SECONDS = 12;

export default {
  name: 'PaymentResultView',
  setup() {
    const route = useRoute();
    const router = useRouter();

    const result = computed(() => String(route.query.payment || '').trim().toLowerCase());
    const reservationId = computed(() => String(route.query.reservationId || '').trim());
    const normalizeQueryValue = (value) => {
      const normalized = String(value || '').trim();
      if (!normalized || normalized.includes('{CHECKOUT_SESSION_ID}')) {
        return '';
      }
      return normalized;
    };

    const checkoutSessionId = computed(() => normalizeQueryValue(route.query.checkoutSessionId || route.query.session_id || ''));
    const paymentId = computed(() => String(route.query.paymentId || route.query.payment_id || '').trim());

    const uiState = ref(result.value === 'failure' ? 'failure' : 'waiting');
    const backendMessage = ref('');
    const verificationError = ref('');
    const isChecking = ref(false);
    const verifyAttempts = ref(0);
    const redirectCountdown = ref(REDIRECT_DELAY_SECONDS);
    const manualRetryTriggered = ref(false);

    let retryTimer = null;
    let redirectTimer = null;

    const clearTimers = () => {
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      if (redirectTimer) {
        clearInterval(redirectTimer);
        redirectTimer = null;
      }
    };

    const goHome = () => {
      clearTimers();
      router.replace('/');
    };

    const startSuccessRedirect = () => {
      if (redirectTimer) {
        return;
      }
      redirectCountdown.value = REDIRECT_DELAY_SECONDS;
      redirectTimer = setInterval(() => {
        redirectCountdown.value -= 1;
        if (redirectCountdown.value <= 0) {
          goHome();
        }
      }, 1000);
    };

    const finishWaitingState = () => {
      if (uiState.value === 'success' || uiState.value === 'failure') {
        return;
      }
      uiState.value = 'waiting';
      if (!backendMessage.value) {
        backendMessage.value = 'Clover ya recibió tu pago o estamos cerrando la validación interna. Recibirás un correo en cuanto terminemos de procesarlo.';
      }
    };

    const scheduleRetry = () => {
      if (verifyAttempts.value >= MAX_VERIFY_ATTEMPTS) {
        if (!manualRetryTriggered.value) {
          retryManualVerification(true);
          return;
        }
        finishWaitingState();
        return;
      }
      retryTimer = setTimeout(() => {
        runVerificationCycle();
      }, VERIFY_DELAY_MS);
    };

    const runVerificationCycle = async () => {
      if (!reservationId.value || result.value !== 'success') {
        return;
      }
      if (isChecking.value) {
        return;
      }
      if (verifyAttempts.value >= MAX_VERIFY_ATTEMPTS) {
        finishWaitingState();
        return;
      }

      clearTimeout(retryTimer);
      retryTimer = null;
      isChecking.value = true;
      verificationError.value = '';
      verifyAttempts.value += 1;

      try {
        const response = await getReservationPaymentStatusRequest(reservationId.value);
        backendMessage.value = response?.message || '';
        if (String(response?.paymentStatus || '').toLowerCase() === 'paid') {
          uiState.value = 'success';
          startSuccessRedirect();
          return;
        }
        if (String(response?.paymentStatus || '').toLowerCase() === 'payment_failed') {
          uiState.value = 'failure';
          return;
        }
        scheduleRetry();
      } catch (error) {
        verificationError.value = error.message || 'No pudimos verificar el pago en este momento.';
        scheduleRetry();
      } finally {
        isChecking.value = false;
      }
    };

    const retryManualVerification = async () => {
      if (!reservationId.value || isChecking.value) {
        return;
      }

      manualRetryTriggered.value = true;
      isChecking.value = true;
      verificationError.value = '';

      try {
        const response = await verifyReservationPaymentRequest(
          reservationId.value,
          checkoutSessionId.value || '',
          paymentId.value || ''
        );
        backendMessage.value = response?.message || '';
        if (String(response?.paymentStatus || '').toLowerCase() === 'paid') {
          uiState.value = 'success';
          startSuccessRedirect();
          return;
        }
        if (String(response?.paymentStatus || '').toLowerCase() === 'payment_failed') {
          uiState.value = 'failure';
          return;
        }
        finishWaitingState();
      } catch (error) {
        verificationError.value = error.message || 'No pudimos ejecutar la verificación manual.';
        finishWaitingState();
      } finally {
        isChecking.value = false;
      }
    };

    onMounted(() => {
      if (result.value === 'failure') {
        uiState.value = 'failure';
        backendMessage.value = 'El pago no se completó en Clover. Puedes volver a intentar con una nueva solicitud cuando lo desees.';
        return;
      }

      if (result.value !== 'success') {
        uiState.value = 'waiting';
        backendMessage.value = 'Estamos terminando de validar tu pago con Clover.';
        scheduleRetry();
        return;
      }

      backendMessage.value = 'Estamos validando el pago con Clover para registrar tu solicitud correctamente.';
      runVerificationCycle();
    });

    onBeforeUnmount(() => {
      clearTimers();
    });

    const heroTitle = computed(() => {
      if (uiState.value === 'success') return 'Pago recibido';
      if (uiState.value === 'failure') return 'Pago no completado';
      return 'Validando tu pago';
    });

    const heroDescription = computed(() => {
      if (uiState.value === 'success') {
        return 'Recibimos la respuesta de Clover y tu solicitud quedó registrada para revisión final del restaurante.';
      }
      if (uiState.value === 'failure') {
        return 'Tu reservación no se registró como pagada. Puedes volver a intentar el proceso cuando estés listo.';
      }
      return 'Estamos cerrando la validación entre Clover y el sistema de reservaciones para evitar duplicados y dejar tu solicitud en el estado correcto.';
    });

    const statusTitle = computed(() => {
      if (uiState.value === 'success') return 'Pago procesado exitosamente';
      if (uiState.value === 'failure') return 'No pudimos completar el pago';
      return 'Validando la confirmación de Clover';
    });

    const statusMessage = computed(() => (
      backendMessage.value
      || (uiState.value === 'failure'
        ? 'El pago fue rechazado o cancelado antes de completarse.'
        : 'Estamos confirmando tu pago con Clover. Esto puede tardar unos segundos.')
    ));

    const statusCardClass = computed(() => {
      if (uiState.value === 'success') return 'border-emerald-200 bg-emerald-50 text-emerald-900';
      if (uiState.value === 'failure') return 'border-red-200 bg-red-50 text-red-900';
      return 'border-amber-200 bg-amber-50 text-amber-900';
    });

    const iconClass = computed(() => {
      if (uiState.value === 'success') return 'bg-emerald-100 text-emerald-700';
      if (uiState.value === 'failure') return 'bg-red-100 text-red-700';
      return 'bg-amber-100 text-amber-700';
    });

    const nextStepLabel = computed(() => {
      if (uiState.value === 'success') {
        return 'Tu solicitud está pagada y queda pendiente de revisión del restaurante.';
      }
      if (uiState.value === 'failure') {
        return 'Vuelve al inicio y crea una nueva solicitud si deseas intentar el pago otra vez.';
      }
      return 'Sigue en esta pantalla mientras terminamos de confirmar la respuesta de Clover.';
    });

    const canRetryVerification = computed(() => result.value === 'success' && uiState.value !== 'success');
    const showCountdown = computed(() => uiState.value === 'success');

    return {
      uiState,
      backendMessage,
      verificationError,
      isChecking,
      reservationId,
      checkoutSessionId,
      paymentId,
      redirectCountdown,
      heroTitle,
      heroDescription,
      statusTitle,
      statusMessage,
      statusCardClass,
      iconClass,
      nextStepLabel,
      canRetryVerification,
      showCountdown,
      goHome,
      runVerificationCycle,
      retryManualVerification,
      logo: logoImage
    };
  }
};
</script>
